import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useCustomerInvoices } from '@/hooks/customer/useCustomerInvoices';
import { extractJsonApiErrorMessage } from '@/lib/jsonapi/errors';
import type { CustomerInvoiceStatus } from '@/types/customer-invoices';

function formatDate(value: string | null, locale: string) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(parsed);
}

function formatMoney(amountMinor: number, currency: string, locale: string) {
  try {
    const formatter = new Intl.NumberFormat(locale, { style: 'currency', currency });
    const fractionDigits = formatter.resolvedOptions().maximumFractionDigits;
    return formatter.format(amountMinor / 10 ** fractionDigits);
  } catch {
    return `${currency} ${(amountMinor / 100).toFixed(2)}`;
  }
}

function formatPeriod(start: string | null, end: string | null, locale: string) {
  if (!start || !end) return null;
  const formattedStart = formatDate(start, locale);
  const formattedEnd = formatDate(end, locale);
  if (formattedStart === '—' || formattedEnd === '—') return null;
  return `${formattedStart} – ${formattedEnd}`;
}

function statusLabelKey(status: CustomerInvoiceStatus) {
  return `customer.billing.invoices.status.${status}` as const;
}

export default function CustomerInvoicesTab() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language ?? 'en';

  const invoicesQuery = useCustomerInvoices(20);

  const invoices = useMemo(
    () => invoicesQuery.data?.pages.flatMap((page) => page.invoices) ?? [],
    [invoicesQuery.data],
  );

  const hasMore = Boolean(invoicesQuery.hasNextPage);

  if (invoicesQuery.isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('customer.billing.tabs.invoices')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('customer.billing.invoices.columns.date')}</TableHead>
                <TableHead>{t('customer.billing.invoices.columns.number')}</TableHead>
                <TableHead>{t('customer.billing.invoices.columns.status')}</TableHead>
                <TableHead>{t('customer.billing.invoices.columns.amount')}</TableHead>
                <TableHead className="hidden lg:table-cell">{t('customer.billing.invoices.columns.period')}</TableHead>
                <TableHead className="text-right">{t('customer.billing.invoices.columns.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 6 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Skeleton className="h-4 w-28" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-6 w-16 rounded-full" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-20" />
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <Skeleton className="h-4 w-48" />
                  </TableCell>
                  <TableCell className="text-right">
                    <Skeleton className="ml-auto h-9 w-28" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  }

  if (invoicesQuery.isError) {
    const status = (invoicesQuery.error as { response?: { status?: number } })?.response?.status;
    const isAuthError = status === 401 || status === 403;
    const title = isAuthError
      ? t('customer.billing.invoices.authErrorTitle')
      : t('customer.billing.invoices.loadErrorTitle');
    const description = isAuthError
      ? t('customer.billing.invoices.authErrorDescription')
      : extractJsonApiErrorMessage(invoicesQuery.error, t('customer.billing.invoices.loadErrorDescription'));

    return (
      <Alert variant="destructive">
        <AlertTitle>{title}</AlertTitle>
        <AlertDescription className="mt-2 space-y-3">
          <p>{description}</p>
          <Button type="button" variant="secondary" onClick={() => invoicesQuery.refetch()}>
            {t('customer.billing.invoices.retry')}
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  if (!invoices.length) {
    return (
      <Alert>
        <AlertTitle>{t('customer.billing.invoices.emptyTitle')}</AlertTitle>
        <AlertDescription>{t('customer.billing.invoices.emptyDescription')}</AlertDescription>
      </Alert>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('customer.billing.tabs.invoices')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead scope="col">{t('customer.billing.invoices.columns.date')}</TableHead>
              <TableHead scope="col">{t('customer.billing.invoices.columns.number')}</TableHead>
              <TableHead scope="col">{t('customer.billing.invoices.columns.status')}</TableHead>
              <TableHead scope="col">{t('customer.billing.invoices.columns.amount')}</TableHead>
              <TableHead scope="col" className="hidden lg:table-cell">
                {t('customer.billing.invoices.columns.period')}
              </TableHead>
              <TableHead scope="col" className="text-right">
                {t('customer.billing.invoices.columns.actions')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map((invoice) => {
              const issued = invoice.issuedAt ?? invoice.paidAt;
              const period = formatPeriod(invoice.billingPeriodStart, invoice.billingPeriodEnd, locale);
              const numberLabel = invoice.number ?? '—';

              return (
                <TableRow key={invoice.id}>
                  <TableCell>{formatDate(issued, locale)}</TableCell>
                  <TableCell className="font-medium">{numberLabel}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{t(statusLabelKey(invoice.status))}</Badge>
                  </TableCell>
                  <TableCell>{formatMoney(invoice.amountDue, invoice.currency, locale)}</TableCell>
                  <TableCell className="hidden lg:table-cell">{period ?? '—'}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {invoice.hostedInvoiceUrl ? (
                        <Button asChild size="sm" variant="outline">
                          <a href={invoice.hostedInvoiceUrl} target="_blank" rel="noreferrer">
                            <ExternalLink className="mr-2 h-4 w-4" />
                            {t('customer.billing.invoices.view')}
                          </a>
                        </Button>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {hasMore ? (
          <div className="flex justify-center">
            <Button
              type="button"
              variant="outline"
              onClick={() => invoicesQuery.fetchNextPage()}
              disabled={invoicesQuery.isFetchingNextPage}
            >
              {invoicesQuery.isFetchingNextPage
                ? t('customer.billing.invoices.loadingMore')
                : t('customer.billing.invoices.loadMore')}
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
